import React from 'react'
import { Tag } from '../types/models/tag'
import { getContrastYIQ } from '../utils'
import { useGetTagsQuery } from '../services/tagApi'
import { useAppSelector } from '../store/hooks'
import { selectSelectedProjectId } from '../store/slices/projectSlice'
import { useGetProjectQuery } from '../services/projectsApi'

//
interface PillsClassificationProps {
  handleTagRemove?: (event: React.MouseEvent<HTMLDivElement, MouseEvent>) => void
  tagIds?: string[] | undefined
  tagNames?: string[] | undefined
  tagConfidence?: number[] | undefined
  className?: string
  maxTags?: number
  large?: boolean
  additionalCount?: number
}

const PillsClassification: React.FC<PillsClassificationProps> = ({
  tagIds,
  tagNames,
  tagConfidence,
  className,
  handleTagRemove,
  maxTags = 3,
  large = false,
  additionalCount,
}) => {
  const selectedProjectId = useAppSelector(selectSelectedProjectId)
  const { data: project } = useGetProjectQuery(selectedProjectId || '', {
    skip: !selectedProjectId,
  })
  const { data: tags } = useGetTagsQuery(
    {
      project_id: selectedProjectId || '',
      dataset_id: project?.datasetid || '',
    },
    { skip: !selectedProjectId || !project?.datasetid }
  )

  const renderPlusTag = (remainingCount: number) => {
    return (
      <div
        style={{
          userSelect: 'none',
          background: 'linear-gradient(135deg, #402563 0%, #114ee0 100%)',
          color: '#ffffff',
          padding: large ? '0.5rem 1rem' : '0.25rem 0.5rem',
          height: large ? '2.4rem' : '1.2rem',
          borderRadius: '9999px',
          fontSize: large ? '1rem' : '0.6rem',
          fontWeight: 'bold',
          display: 'flex',
          alignItems: 'center',
          boxShadow: '0 2px 4px rgba(0, 0, 0, 0.2)',
          border: '1px solid rgba(255, 255, 255, 0.1)',
        }}
      >
        <span>+{remainingCount}</span>
      </div>
    )
  }

  // Early return if no valid data
  const hasValidTagNames =
    tagNames && tagNames.length > 0 && tagNames.some((name) => name && name.trim() !== '')
  const hasValidTagIds = tagIds && tagIds.length > 0 && tagIds.some((id) => id && id.trim() !== '')

  if (!hasValidTagNames && !hasValidTagIds) {
    return null
  }

  return (
    <div
      className={`class-pills ${className || ''}`}
      style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}
    >
      {tagNames &&
        tagNames.slice(0, maxTags).map((tagName, index) => {
          const fullTag = tags?.tags?.find((tag) => tag.name === tagName)
          const backgroundColor = `#${
            fullTag?.color.toString(16).padStart(6, '0').slice(0, 6) || '#800085'
          }`
          const textColor = getContrastYIQ(backgroundColor)

          return (
            <div
              key={index}
              onClick={handleTagRemove}
              style={{
                backgroundColor,
                color: textColor,
                padding: large ? '0.5rem 1rem' : '0.25rem 0.5rem',
                height: large ? '2.4rem' : '1.2rem',
                borderRadius: '9999px',
                fontSize: large ? '1rem' : '0.6rem',
                fontWeight: 'bold',
                display: 'flex',
                alignItems: 'center',
              }}
            >
              {tagConfidence && (
                <span>
                  {tagName + ' (' + Math.round((tagConfidence?.[index] || 0) * 100) + '%)'}
                </span>
              )}
              {!tagConfidence && <span>{tagName}</span>}
            </div>
          )
        })}
      {tagNames && tagNames.length > maxTags && renderPlusTag(tagNames.length - maxTags)}
      {additionalCount !== undefined && additionalCount > 0 && renderPlusTag(additionalCount)}

      {tagIds && (
        <>
          {tagIds.slice(0, maxTags).map((tagId, index) => {
            const tag = tags?.tags?.find((t: Tag) => t.id === tagId)
            if (!tag) return null

            const backgroundColor = `#${tag.color.toString(16).padStart(6, '0').slice(0, 6)}`
            const textColor = getContrastYIQ(backgroundColor)

            return (
              <div
                key={`${tag.id}-${index}`}
                onClick={handleTagRemove}
                style={{
                  backgroundColor,
                  color: textColor,
                  padding: large ? '0.5rem 1rem' : '0.25rem 0.5rem',
                  height: large ? '2.4rem' : '1.2rem',
                  borderRadius: '9999px',
                  fontSize: large ? '1.5rem' : '0.75rem',
                  fontWeight: 'bold',
                  display: 'flex',
                  alignItems: 'center',
                }}
              >
                {tagConfidence && (
                  <span>
                    {tag.name + ' (' + Math.round((tagConfidence?.[index] || 0) * 100) + '%)'}
                  </span>
                )}
                {!tagConfidence && <span>{tag.name}</span>}
              </div>
            )
          })}
          {tagIds.length > maxTags && renderPlusTag(tagIds.length - maxTags)}
        </>
      )}
    </div>
  )
}

export default PillsClassification
